import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PageHeader from 'components/PageHeader';
import PageFooter from 'components/PageFooter';
import { winners2025 } from 'data/winners2025';
import { winners2024 } from 'data/winners2024';
import { winners2023 } from 'data/winners2023';
import { ArrowLeftIcon } from '@heroicons/react/20/solid';
import { TrophyIcon, UserGroupIcon } from '@heroicons/react/24/outline';

import { Winner } from 'types/winner';

export default function WinnerDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const allWinners: Winner[] = [
    ...winners2025,
    ...winners2024,
    ...winners2023,
  ] as Winner[];
  const winner = allWinners.find(w => w.id === id);

  if (!winner) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageHeader />
        <div className="max-w-6xl mx-auto px-6 py-20 text-center">
          <h1 className="text-2xl font-bold text-gray-800 mb-4">
            Winner not found
          </h1>
          <button
            onClick={() => navigate('/')}
            className="text-smdd-red hover:underline font-bold"
          >
            Back to Home
          </button>
        </div>
        <PageFooter />
      </div>
    );
  }

  const teamMembers = winner.teamMembers || [];
  const detailsContent = winner.details || '';

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader />

      <main className="mx-auto max-w-6xl px-6 py-8">
        <div className="mb-6 flex w-full">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-700 hover:text-gray-900 transition-colors"
          >
            <ArrowLeftIcon className="h-8 w-8" />
            <p className="text-lg font-bold">Project Details</p>
          </button>
        </div>

        <div className="bg-white rounded-lg p-8 shadow-md">
          {/* Top Section with Team Photo */}
          <div className="mb-8 grid grid-cols-1 gap-8 lg:grid-cols-3">
            {/* Left Content */}
            <div className="lg:col-span-2">
              {/* Title and Subtitle */}
              <div className="mb-6">
                <h1 className="mb-2 text-3xl font-bold text-smdd-red font-bai">
                  {winner.name}
                </h1>
                <p className="text-gray-500 font-bai">{winner.organization}</p>
              </div>

              {/* Award Badge */}
              <div className="flex flex-row gap-2 mb-6">
                <div className="inline-flex items-center gap-2 rounded-full border border-gray-400 px-4 py-1">
                  <span className="text-sm font-medium text-smdd-red">
                    <TrophyIcon className="w-4 h-4" />
                  </span>
                  <p className="text-sm font-bai">
                    {winner.award} {winner.year && `(${winner.year})`}
                  </p>
                </div>
                <div className="inline-flex items-center gap-1 rounded-lg bg-blue-100 px-3 py-1 text-xs text-blue-600 font-bold uppercase tracking-wider">
                  <UserGroupIcon className="w-4 h-4 stroke-2" />
                  <span>
                    {winner.category === 'individual'
                      ? 'Individual'
                      : 'Organizations / Teams'}
                  </span>
                </div>
              </div>

              {/* Company Section */}
              <div className="mb-8 space-y-4">
                <div className="flex flex-row gap-8 sm:gap-16">
                  <div>
                    <p className="mb-2 text-sm font-semibold text-gray-700 font-bai">
                      Company
                    </p>
                    <p className="text-gray-600 text-sm font-bai">
                      {winner.organization}
                    </p>
                  </div>
                  <div>
                    <p className="mb-2 text-sm font-semibold text-gray-700 font-bai">
                      Pillar
                    </p>
                    <p className="text-gray-600 text-sm font-bai">
                      {winner.pillar || 'N/A'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Team Lead Section */}
              <div className="mb-8">
                <p className="mb-4 font-semibold text-gray-900 font-bai">
                  Team Lead
                </p>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-900 font-bai">
                      {winner.name}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 italic font-bai">
                    {winner.title || 'Team Lead'}
                  </p>
                </div>
              </div>

              {/* Team Members Section */}
              {teamMembers.length > 0 && (
                <div className="mb-8">
                  <p className="mb-4 font-semibold text-gray-900 font-bai">
                    Team Members
                  </p>
                  <div className="grid grid-cols-2 gap-6 md:grid-cols-4">
                    {teamMembers.map(member => (
                      <div key={member.name}>
                        <div className="flex items-center gap-2">
                          <span className="text-gray-900 text-sm font-bai">
                            {member.name}
                          </span>
                        </div>
                        <p className="text-xs text-gray-600 italic font-bai">
                          {member.role}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Team Photo */}
            <div className="lg:col-span-1">
              <div className="flex flex-col items-center lg:items-end gap-2">
                {/* <div className="h-64 sm:w-full overflow-hidden rounded-xl bg-gray-200 aspect-[4/3] lg:aspect-auto">
                  <img
                    src="https://images.unsplash.com/photo-1552664730-d307ca884978?w=500&h=400&fit=crop"
                    alt="Team Photo"
                    className="h-full w-full object-cover"
                  />
                </div> */}
              </div>
            </div>
          </div>

          <hr className="my-8" />

          {/* Project Details Sections */}
          <div className="mb-8">
            <AdditionalComments additionalComments={detailsContent} />
          </div>

          {/* Media & Resources Section */}
          {/* {(winner.projectLink || winner.videoUrl) && (
            <div className="mt-8 pt-8 border-t border-gray-100">
              <h3 className="text-lg font-bold text-gray-900 mb-4 font-bai">
                Media & Resources
              </h3>
              <div className="flex flex-wrap gap-4">
                {winner.projectLink && winner.projectLink !== '#' && (
                  <a
                    href={winner.projectLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-gray-900 text-white px-6 py-2.5 rounded-lg hover:bg-gray-800 transition-colors font-bai text-sm font-semibold"
                  >
                    <DocumentIcon className="w-5 h-5" />
                    Project Presentation
                  </a>
                )}
                {winner.videoUrl && (
                  <a
                    href={winner.videoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-smdd-red text-white px-6 py-2.5 rounded-lg hover:bg-red-700 transition-colors font-bai text-sm font-semibold"
                  >
                    <PlayIcon className="w-5 h-5" />
                    Project Video
                  </a>
                )}
              </div>
            </div>
          )} */}
        </div>
      </main>

      <PageFooter />
    </div>
  );
}

interface AdditionalCommentsProps {
  additionalComments: string;
}

function AdditionalComments({ additionalComments }: AdditionalCommentsProps) {
  const lines = additionalComments.split('\n');
  return (
    <div className="space-y-1">
      {lines.map((line, index) => {
        const trimmedLine = line.trim();
        if (!trimmedLine) return <div key={index} className="h-2" />;

        const isHeader =
          trimmedLine.startsWith('===') && trimmedLine.endsWith('===');
        const isBullet = trimmedLine.startsWith('•');

        if (isHeader) {
          return (
            <h4
              key={index}
              className="text-base font-bold text-smdd-red mt-6 mb-2 font-bai first:mt-0"
            >
              {trimmedLine.replace(/===/g, '').trim()}
            </h4>
          );
        }

        if (isBullet) {
          return (
            <div key={index} className="flex gap-3 mb-1.5 pl-1">
              <span className="font-bold flex-shrink-0">•</span>
              <p className="text-sm text-gray-700 leading-relaxed font-bai">
                {trimmedLine.replace('•', '').trim()}
              </p>
            </div>
          );
        }

        return (
          <p
            key={index}
            className="text-sm text-gray-700 leading-relaxed mb-2 font-bai"
          >
            {trimmedLine}
          </p>
        );
      })}
    </div>
  );
}
